import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createProduct } from '../../services/api';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'video/mp4', 'video/quicktime', 'video/webm', 'image/heic', 'image/heif'];
const MAX_FILES = 10;
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

interface UploadProps {
  onProductsUpdated?: () => void;
}

export const UploadConsole: React.FC<UploadProps> = ({ onProductsUpdated }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [resultCode, setResultCode] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [activePreview, setActivePreview] = useState(0);
  const previewUrls = useRef<string[]>([]);

  // Generate object URLs for previews, cleanup on unmount
  useEffect(() => {
    return () => {
      previewUrls.current.forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  const getPreviewUrl = (file: File, index: number): string => {
    if (!previewUrls.current[index]) {
      previewUrls.current[index] = URL.createObjectURL(file);
    }
    return previewUrls.current[index];
  };

  // Reset active preview index when files change
  const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const selected = Array.from(e.target.files || []);
    if (selected.length === 0) return;

    // Check file size
    const oversized = selected.find(f => f.size > MAX_FILE_SIZE);
    if (oversized) {
      setError(`El archivo "${oversized.name}" excede 50MB. Redimensiona o comprime antes de subir.`);
      setStatus('error');
      return;
    }

    // Check total files limit
    const total = files.length + selected.length;
    if (total > MAX_FILES) {
      setError(`Máximo ${MAX_FILES} archivos por producto. Ya tienes ${files.length} seleccionado(s).`);
      setStatus('error');
      return;
    }

    setFiles(prev => [...prev, ...selected]);
    setActivePreview(0);
  };

  const removeFile = (index: number) => {
    setFiles(prev => {
      const updated = prev.filter((_, i) => i !== index);
      // Revoke the removed URL
      if (previewUrls.current[index]) {
        URL.revokeObjectURL(previewUrls.current[index]);
        previewUrls.current[index] = '';
      }
      // Shift remaining URLs
      previewUrls.current.splice(index, 1);
      return updated;
    });
    if (activePreview >= files.length - 1) {
      setActivePreview(Math.max(0, files.length - 2));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    // Local validation
    if (files.length === 0) {
      setError('Selecciona al menos una foto o video.');
      setStatus('error');
      return;
    }
    if (!name.trim()) {
      setError('El nombre del artículo es obligatorio.');
      setStatus('error');
      return;
    }
    if (!price || isNaN(parseFloat(price)) || parseFloat(price) <= 0) {
      setError('Precio inválido. Ingresa un número mayor a 0.');
      setStatus('error');
      return;
    }

    setSubmitting(true);
    setStatus('loading');
    setError(null);

    try {
      const formData = new FormData();
      files.forEach(f => formData.append('media', f));
      formData.append('name', name.trim());
      formData.append('price', price);
      if (category.trim()) formData.append('category', category.trim());
      if (description.trim()) formData.append('description', description.trim());

      const result = await createProduct(formData);
      setResultCode(result.code);
      setStatus('success');
      if (onProductsUpdated) onProductsUpdated();
    } catch (err: any) {
      const msg = err?.message || err?.response?.data?.message || 'Error al subir el artículo. Intenta de nuevo.';
      setError(msg);
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setStatus('idle');
    setFiles([]);
    setName('');
    setPrice('');
    setCategory('');
    setDescription('');
    setError(null);
    setResultCode(null);
    setFormKey(k => k + 1); // Recreate DOM → fresh file input
    setActivePreview(0);
    // Revoke all preview URLs
    previewUrls.current.forEach(url => URL.revokeObjectURL(url));
    previewUrls.current = [];
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <button onClick={() => navigate('/admin')}
        className="text-purple-600 hover:text-purple-800 font-medium mb-4 block">
        ← Volver al panel
      </button>

      <h1 className="text-3xl font-bold text-gray-900 mb-2">📤 Subir producto</h1>
      <p className="text-gray-600 mb-6">Agrega un nuevo artículo a la tienda</p>

      {/* Preview block: appears when files are selected */}
      {files.length > 0 && status === 'idle' && (
        <div className="mb-6 bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
          {/* Main preview */}
          <div className="relative bg-black/5 flex items-center justify-center min-h-[250px] sm:min-h-[320px]">
            {files[activePreview]?.type.startsWith('video/') ? (
              <video
                key={activePreview}
                src={getPreviewUrl(files[activePreview], activePreview)}
                controls
                preload="metadata"
                className="max-h-80 sm:max-h-96 w-full object-contain"
              />
            ) : (
              <img
                key={activePreview}
                src={getPreviewUrl(files[activePreview], activePreview)}
                alt={`Preview ${activePreview + 1}`}
                className="max-h-80 sm:max-h-96 w-full object-contain p-2"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                  (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                }}
              />
            )}
            {/* Fallback for HEIC/HEIF or broken images */}
            <div className="hidden absolute inset-0 flex items-center justify-center text-gray-400 text-sm">
              🖼️ Vista previa no disponible: {files[activePreview]?.name}
            </div>
          </div>

          {/* Navigation: prev/next + counter */}
          {files.length > 1 && (
            <div className="flex items-center justify-between px-4 py-2 bg-white border-t border-gray-200">
              <button
                type="button"
                onClick={() => setActivePreview(p => Math.max(0, p - 1))}
                disabled={activePreview === 0}
                className="text-sm text-purple-600 hover:text-purple-800 disabled:text-gray-300 disabled:cursor-not-allowed font-medium"
              >
                ◄ Anterior
              </button>
              <span className="text-sm text-gray-500 font-medium">
                {activePreview + 1} / {files.length}
              </span>
              <button
                type="button"
                onClick={() => setActivePreview(p => Math.min(files.length - 1, p + 1))}
                disabled={activePreview === files.length - 1}
                className="text-sm text-purple-600 hover:text-purple-800 disabled:text-gray-300 disabled:cursor-not-allowed font-medium"
              >
                Siguiente ►
              </button>
            </div>
          )}

          {/* Thumbnail strip */}
          {files.length > 1 && (
            <div className="flex gap-2 px-4 py-3 bg-white border-t border-gray-100 overflow-x-auto">
              {files.map((f, i) => {
                const isImage = f.type.startsWith('image/');
                const isActive = i === activePreview;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setActivePreview(i)}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      isActive ? 'border-purple-500 ring-2 ring-purple-200' : 'border-gray-200 opacity-70 hover:opacity-100'
                    }`}
                  >
                    {isImage ? (
                      <img
                        src={getPreviewUrl(f, i)}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                          (e.target as HTMLImageElement).parentElement!.innerText = '📷';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-100 text-lg">
                        🎬
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {status === 'loading' && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">
            Subiendo artículo... <strong>{name}</strong>
          </p>
          {files.length > 0 && (
            <p className="text-sm text-gray-400 mt-2">{files.length} archivo(s)</p>
          )}
        </div>
      )}

      {status === 'success' && (
        <div className="text-center py-8 bg-green-50 rounded-xl border-2 border-green-300">
          <div className="text-5xl mb-4">✅</div>
          <h2 className="text-2xl font-bold text-green-800 mb-2">¡Artículo creado!</h2>
          <p className="text-gray-700 mb-1">
            Código: <strong className="text-lg">{resultCode}</strong>
          </p>
          <p className="text-gray-500 mb-6">{name}</p>
          <div className="flex gap-3 justify-center flex-wrap">
            <button
              onClick={() => navigate('/admin/op/' + resultCode)}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition-colors"
            >
              👁️ Ver en consola
            </button>
            <button
              onClick={resetForm}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors"
            >
              📤 Subir otro artículo
            </button>
          </div>
        </div>
      )}

      {status === 'error' && (
        <div className="text-center py-8 bg-red-50 rounded-xl border-2 border-red-300 mb-6">
          <div className="text-5xl mb-4">❌</div>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={() => { setStatus('idle'); setError(null); }}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors"
          >
            Intentar de nuevo
          </button>
        </div>
      )}

      {status === 'idle' && (
        <form key={formKey} onSubmit={handleSubmit} className="space-y-5">
          {/* File input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fotos / Videos <span className="text-gray-400 font-normal">(máx. {MAX_FILES} archivos)</span>
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-purple-400 transition-colors relative">
              <input
                id="upload-file-input"
                type="file"
                multiple
                accept="image/*,video/*"
                onChange={handleFiles}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                style={{ fontSize: 0, zIndex: 10 }}
              />
              <label htmlFor="upload-file-input" className="cursor-pointer block relative z-0 pointer-events-none">
                <span className="text-purple-600 font-medium text-lg">
                  📁 {files.length === 0 ? 'Seleccionar archivos' : `${files.length} archivo(s) seleccionado(s)`}
                </span>
                <p className="text-xs text-gray-400 mt-1">
                  Fotos: JPG, PNG, WebP, HEIC | Videos: MP4, MOV, WebM
                </p>
                <p className="text-xs text-gray-400">
                  Máx. 50MB por archivo
                </p>
              </label>
            </div>
            {/* File list */}
            {files.length > 0 && (
              <ul className="mt-2 space-y-1">
                {files.map((f, i) => (
                  <li key={i} className="flex items-center justify-between text-sm text-gray-600 bg-gray-50 px-3 py-1.5 rounded-lg">
                    <span className="truncate max-w-[70%]">
                      {f.type.startsWith('video/') ? '🎬' : '🖼️'} {f.name}
                    </span>
                    <span className="text-xs text-gray-400">
                      {(f.size / 1024 / 1024).toFixed(1)} MB
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="text-red-500 hover:text-red-700 ml-2 text-sm font-medium"
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre del artículo <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-colors"
              placeholder="Ej: Blusa Floral Bordada"
              maxLength={200}
              required
            />
          </div>

          {/* Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Precio <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={price}
              onChange={e => setPrice(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-colors"
              placeholder="Ej: 1500"
              min="1"
              step="1"
              required
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
            <input
              type="text"
              value={category}
              onChange={e => setCategory(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-colors"
              placeholder="Ej: Blusas"
              maxLength={100}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-colors"
              placeholder="Ej: Bordada a mano, talla M, color azul"
              maxLength={2000}
              rows={3}
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 bg-purple-600 text-white rounded-xl font-bold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-lg"
          >
            📤 Subir artículo
          </button>
        </form>
      )}
    </div>
  );
};
